import { LEAD_FLAG_SYSTEM } from '#src/codeSystems.ts'
import {
  type ParticipantFlagDefinition,
  ParticipantFlagPriority,
} from '#src/models/patients/index.ts'

export const LeadFlagCode = {
  // Interest Category
  INTEREST_VERY_INTERESTED: 'interest-very-interested',
  INTEREST_LUKEWARM: 'interest-lukewarm',
  INTEREST_NOT_INTERESTED: 'interest-not-interested',
  INTEREST_COLD: 'interest-cold',

  // Issue Category
  ISSUE_IHSS: 'issue-ihss',
  ISSUE_PCP: 'issue-pcp',

  // Source Category
  SOURCE_AFFORDABLE_HOUSING: 'source-affordable-housing',
  SOURCE_SENIOR_CENTERS: 'source-senior-centers',
  SOURCE_ORGANIC: 'source-organic',
  SOURCE_COMMUNITY_REFERRALS: 'source-community-referrals',

  // Insurance Category
  DUAL: 'dual',
  MEDI_CAL_ONLY: 'medi-cal-only',

  // Status Category
  ASTRANA: 'astrana',
  EXISTING_APPOINTMENTS: 'existing-appointments',

  // Relationship Category
  COUPLE: 'couple',

  // Competitor Category
  WELBE: 'welbe',
} as const
export type LeadFlagCode = (typeof LeadFlagCode)[keyof typeof LeadFlagCode]

export const LeadFlagCategory = {
  INTEREST: 'Interest',
  ISSUE: 'Issue',
  SOURCE: 'Source',
  INSURANCE: 'Insurance',
  STATUS: 'Status',
  RELATIONSHIP: 'Relationship',
  COMPETITOR: 'Competitor',
} as const
export type LeadFlagCategory =
  (typeof LeadFlagCategory)[keyof typeof LeadFlagCategory]

export interface LeadFlagDefinition extends ParticipantFlagDefinition {
  category: LeadFlagCategory
}

export const LEAD_FLAGS: Record<LeadFlagCode, LeadFlagDefinition> = {
  [LeadFlagCode.INTEREST_VERY_INTERESTED]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.INTEREST_VERY_INTERESTED,
      display: '🟢 Very interested',
    },
    category: LeadFlagCategory.INTEREST,
    priority: ParticipantFlagPriority.P3,
  },
  [LeadFlagCode.INTEREST_LUKEWARM]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.INTEREST_LUKEWARM,
      display: '🟡 Lukewarm',
    },
    category: LeadFlagCategory.INTEREST,
    priority: ParticipantFlagPriority.P3,
  },
  [LeadFlagCode.INTEREST_NOT_INTERESTED]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.INTEREST_NOT_INTERESTED,
      display: '🔴 Not interested',
    },
    category: LeadFlagCategory.INTEREST,
    priority: ParticipantFlagPriority.P3,
  },
  [LeadFlagCode.INTEREST_COLD]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.INTEREST_COLD,
      display: '🔵 Cold',
    },
    category: LeadFlagCategory.INTEREST,
    priority: ParticipantFlagPriority.P3,
  },
  [LeadFlagCode.ISSUE_IHSS]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.ISSUE_IHSS,
      display: 'IHSS',
    },
    category: LeadFlagCategory.ISSUE,
    priority: ParticipantFlagPriority.P0,
  },
  [LeadFlagCode.ISSUE_PCP]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.ISSUE_PCP,
      display: 'PCP',
    },
    category: LeadFlagCategory.ISSUE,
    priority: ParticipantFlagPriority.P0,
  },
  [LeadFlagCode.SOURCE_AFFORDABLE_HOUSING]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.SOURCE_AFFORDABLE_HOUSING,
      display: 'Affordable Housing',
    },
    category: LeadFlagCategory.SOURCE,
    priority: ParticipantFlagPriority.P2,
  },
  [LeadFlagCode.SOURCE_SENIOR_CENTERS]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.SOURCE_SENIOR_CENTERS,
      display: 'Senior Centers',
    },
    category: LeadFlagCategory.SOURCE,
    priority: ParticipantFlagPriority.P2,
  },
  [LeadFlagCode.SOURCE_ORGANIC]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.SOURCE_ORGANIC,
      display: 'Organic',
    },
    category: LeadFlagCategory.SOURCE,
    priority: ParticipantFlagPriority.P2,
  },
  [LeadFlagCode.SOURCE_COMMUNITY_REFERRALS]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.SOURCE_COMMUNITY_REFERRALS,
      display: 'Community Referrals',
    },
    category: LeadFlagCategory.SOURCE,
    priority: ParticipantFlagPriority.P2,
  },
  [LeadFlagCode.DUAL]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.DUAL,
      display: 'Dual',
    },
    category: LeadFlagCategory.INSURANCE,
    priority: ParticipantFlagPriority.P1,
  },
  [LeadFlagCode.MEDI_CAL_ONLY]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.MEDI_CAL_ONLY,
      display: 'Medi-Cal Only',
    },
    category: LeadFlagCategory.INSURANCE,
    priority: ParticipantFlagPriority.P1,
  },
  [LeadFlagCode.ASTRANA]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.ASTRANA,
      display: 'Astrana',
    },
    category: LeadFlagCategory.STATUS,
    priority: ParticipantFlagPriority.P0,
  },
  [LeadFlagCode.EXISTING_APPOINTMENTS]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.EXISTING_APPOINTMENTS,
      display: 'Existing Appointments',
    },
    category: LeadFlagCategory.STATUS,
    priority: ParticipantFlagPriority.P1,
  },
  [LeadFlagCode.COUPLE]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.COUPLE,
      display: 'Couple',
    },
    category: LeadFlagCategory.RELATIONSHIP,
    priority: ParticipantFlagPriority.P2,
  },
  [LeadFlagCode.WELBE]: {
    coding: {
      system: LEAD_FLAG_SYSTEM,
      code: LeadFlagCode.WELBE,
      display: 'Welbe',
    },
    category: LeadFlagCategory.COMPETITOR,
    priority: ParticipantFlagPriority.P0,
  },
}
